import { useEffect, useRef } from 'react';
import { loadGsap } from '../../../shared/lib/gsap';
import { useMotionReady } from './useMotionReady';
import { useNearViewport } from './useNearViewport';

/**
 * Layout for a section while this hook pins it: exactly one screen, the heading
 * clear of the sticky banner and header, and the track filling what is left
 * above the call dock. Pinned content cannot scroll out from under either.
 */
export const PINNED_TRACK_LAYOUT =
  'flex h-[100svh] flex-col pb-[6.5rem] pt-[calc(var(--demo-banner-height)+6.25rem)] lg:pb-10';

type UseHorizontalTrackOptions = {
  enabled: boolean;
  /** Extra px of travel past the track so the last card clears the edge. */
  overshoot: number;
  scrub: number;
  /** Fraction of a viewport of pinned hold after the track finishes. */
  tail: number;
  onProgress?: (progress: number) => void;
};

/**
 * Pins a section and scrubs a wide flex track sideways. Shared by the services
 * and companions rails so the two stay in step.
 */
export function useHorizontalTrack<TSection extends HTMLElement>({
  enabled,
  overshoot,
  scrub,
  tail,
  onProgress,
}: UseHorizontalTrackOptions) {
  const sectionRef = useRef<TSection>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const near = useNearViewport(sectionRef);
  const motionReady = useMotionReady();
  const progressHandler = useRef(onProgress);

  useEffect(() => {
    progressHandler.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;

    if (!enabled || !near || !motionReady || !section || !track) {
      return;
    }

    let cancelled = false;
    let animation: gsap.core.Tween | null = null;

    const getScrollAmount = () => Math.max(track.scrollWidth - section.offsetWidth + overshoot, 0);

    void loadGsap().then(({ gsap }) => {
      if (cancelled) {
        return;
      }

      animation = gsap.to(track, {
        x: () => -getScrollAmount(),
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => `+=${getScrollAmount() + window.innerHeight * tail}`,
          pin: true,
          scrub,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => progressHandler.current?.(self.progress),
        },
      });
    });

    return () => {
      cancelled = true;
      animation?.scrollTrigger?.kill();
      animation?.kill();
    };
  }, [enabled, motionReady, near, overshoot, scrub, tail]);

  return { sectionRef, trackRef };
}
